import React, { useEffect, useState } from 'react';
import { SaveIcon, XIcon, AlertTriangleIcon } from 'lucide-react';
import { Budget, BudgetType } from './BudgetManagement';
import { EntitySiteSelector } from '../exchange/EntitySiteSelector';
import { GLCodeInput } from './GLCodeInput';
import { RuleEditor } from './RuleEditor';
import { CurrencySelector } from '../../settings/CurrencySelector';
interface BudgetFormProps {
  budget?: Budget;
  budgetTypes: BudgetType[];
  onSave: (budget: Budget | Omit<Budget, 'id' | 'createdAt' | 'updatedAt'>) => void;
  onCancel: () => void;
  locale: string;
}
export function BudgetForm({
  budget,
  budgetTypes,
  onSave,
  onCancel,
  locale
}: BudgetFormProps) {
  const isEditing = !!budget;
  const [formData, setFormData] = useState<Omit<Budget, 'id' | 'createdAt' | 'updatedAt' | 'budgetTypeName'> & {
    budgetTypeName?: string;
  }>({
    name: '',
    description: '',
    budgetTypeId: '',
    glCode: '',
    entityId: undefined,
    entityName: undefined,
    siteId: undefined,
    siteName: undefined,
    totalAmount: 0,
    remainingAmount: 0,
    currency: 'USD',
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date(new Date().getFullYear(), new Date().getMonth() + 3, new Date().getDate()).toISOString().split('T')[0],
    status: 'Active',
    rules: '{}'
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [scope, setScope] = useState<'Global' | 'Entity' | 'Site'>(budget?.siteId ? 'Site' : budget?.entityId ? 'Entity' : 'Global');
  useEffect(() => {
    if (budget) {
      setFormData({
        name: budget.name,
        description: budget.description,
        budgetTypeId: budget.budgetTypeId,
        budgetTypeName: budget.budgetTypeName,
        glCode: budget.glCode,
        entityId: budget.entityId,
        entityName: budget.entityName,
        siteId: budget.siteId,
        siteName: budget.siteName,
        totalAmount: budget.totalAmount,
        remainingAmount: budget.remainingAmount,
        currency: budget.currency,
        startDate: budget.startDate,
        endDate: budget.endDate,
        status: budget.status,
        rules: budget.rules
      });
    }
  }, [budget]);
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    // Clear error when field is edited
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: undefined
      });
    }
  };
  const handleNumberInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData({
      ...formData,
      [name]: parseFloat(value) || 0
    });
    // Clear error when field is edited
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: undefined
      });
    }
  };
  const handleBudgetTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedTypeId = e.target.value;
    const selectedType = budgetTypes.find(type => type.id === selectedTypeId);
    setFormData({
      ...formData,
      budgetTypeId: selectedTypeId,
      budgetTypeName: selectedType?.name || ''
    });
    // Clear error when field is edited
    if (errors.budgetTypeId) {
      setErrors({
        ...errors,
        budgetTypeId: undefined
      });
    }
  };
  const handleEntitySiteChange = (entityId: string | undefined, entityName: string | undefined, siteId: string | undefined, siteName: string | undefined, newScope: 'Global' | 'Entity' | 'Site') => {
    setScope(newScope);
    setFormData({
      ...formData,
      entityId,
      entityName,
      siteId,
      siteName
    });
    // Clear errors
    setErrors({
      ...errors,
      entityId: undefined,
      siteId: undefined
    });
  };
  const handleGLCodeChange = (glCode: string) => {
    setFormData({
      ...formData,
      glCode
    });
    // Clear error when field is edited
    if (errors.glCode) {
      setErrors({
        ...errors,
        glCode: undefined
      });
    }
  };
  const handleRulesChange = (rulesJson: string) => {
    setFormData({
      ...formData,
      rules: rulesJson
    });
  };
  const handleCurrencyChange = (currency: string) => {
    setFormData({
      ...formData,
      currency
    });
  };
  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Budget name is required';
    }
    if (!formData.budgetTypeId) {
      newErrors.budgetTypeId = 'Budget type is required';
    }
    if (!formData.glCode.trim()) {
      newErrors.glCode = 'GL Code is required';
    }
    if (scope === 'Entity' && !formData.entityId) {
      newErrors.entityId = 'Entity is required for Entity scope';
    }
    if (scope === 'Site' && !formData.siteId) {
      newErrors.siteId = 'Site is required for Site scope';
    }
    if (formData.totalAmount <= 0) {
      newErrors.totalAmount = 'Total amount must be greater than zero';
    }
    if (!formData.startDate) {
      newErrors.startDate = 'Start date is required';
    }
    if (!formData.endDate) {
      newErrors.endDate = 'End date is required';
    }
    if (formData.startDate && formData.endDate && new Date(formData.startDate) > new Date(formData.endDate)) {
      newErrors.endDate = 'End date must be after start date';
    }
    try {
      JSON.parse(formData.rules);
    } catch (e) {
      newErrors.rules = 'Rules must be valid JSON';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      // If creating a new budget, set the remaining amount equal to total amount
      const finalData = isEditing ? formData : {
        ...formData,
        remainingAmount: formData.totalAmount
      };
      onSave(finalData as Budget);
    }
  };
  return <form onSubmit={handleSubmit} className="space-y-8" data-id="element-6647">
      <div className="flex justify-between items-center mb-6" data-id="element-6648">
        <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-6649">
          {isEditing ? 'Edit Budget' : 'Create New Budget'}
        </h3>
        <div className="flex space-x-3" data-id="element-6650">
          <button type="button" onClick={onCancel} className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-6651">
            <XIcon className="h-4 w-4 mr-2" data-id="element-6652" />
            Cancel
          </button>
          <button type="submit" className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-6653">
            <SaveIcon className="h-4 w-4 mr-2" data-id="element-6654" />
            {isEditing ? 'Update Budget' : 'Create Budget'}
          </button>
        </div>
      </div>
      <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-6655">
        <div className="sm:col-span-6" data-id="element-6656">
          <h4 className="text-sm font-medium text-gray-900 mb-3" data-id="element-6657">
            General Information
          </h4>
          <div className="bg-white shadow overflow-hidden sm:rounded-md p-6" data-id="element-6658">
            <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-6659">
              <div className="sm:col-span-4" data-id="element-6660">
                <label htmlFor="name" className="block text-sm font-medium text-gray-700" data-id="element-6661">
                  Budget Name *
                </label>
                <div className="mt-1" data-id="element-6662">
                  <input type="text" name="name" id="name" value={formData.name} onChange={handleInputChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md ${errors.name ? 'border-red-300' : ''}`} data-id="element-6663" />
                  {errors.name && <p className="mt-1 text-sm text-red-600" data-id="element-6664">{errors.name}</p>}
                </div>
              </div>
              <div className="sm:col-span-2" data-id="element-6665">
                <label htmlFor="budgetTypeId" className="block text-sm font-medium text-gray-700" data-id="element-6666">
                  Budget Type *
                </label>
                <div className="mt-1" data-id="element-6667">
                  <select id="budgetTypeId" name="budgetTypeId" value={formData.budgetTypeId} onChange={handleBudgetTypeChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md ${errors.budgetTypeId ? 'border-red-300' : ''}`} data-id="element-6668">
                    <option value="" data-id="element-6669">Select Budget Type</option>
                    {budgetTypes.map(type => <option key={type.id} value={type.id} data-id="element-6670">
                        {type.name}
                      </option>)}
                  </select>
                  {errors.budgetTypeId && <p className="mt-1 text-sm text-red-600" data-id="element-6671">
                      {errors.budgetTypeId}
                    </p>}
                </div>
              </div>
              <div className="sm:col-span-6" data-id="element-6672">
                <label htmlFor="description" className="block text-sm font-medium text-gray-700" data-id="element-6673">
                  Description
                </label>
                <div className="mt-1" data-id="element-6674">
                  <textarea id="description" name="description" rows={3} value={formData.description} onChange={handleInputChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-6675" />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="sm:col-span-6" data-id="element-6676">
          <h4 className="text-sm font-medium text-gray-900 mb-3" data-id="element-6677">
            Budget Scope
          </h4>
          <div className="bg-white shadow overflow-hidden sm:rounded-md p-6" data-id="element-6678">
            <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-6679">
              <div className="sm:col-span-3" data-id="element-6680">
                <label htmlFor="glCode" className="block text-sm font-medium text-gray-700" data-id="element-6681">
                  GL Code *
                </label>
                <div className="mt-1" data-id="element-6682">
                  <GLCodeInput value={formData.glCode} onChange={handleGLCodeChange} error={errors.glCode} data-id="element-6683" />
                </div>
              </div>
              <div className="sm:col-span-6 mt-4" data-id="element-6684">
                <EntitySiteSelector scope={scope} entityId={formData.entityId} entityName={formData.entityName} siteId={formData.siteId} siteName={formData.siteName} onChange={handleEntitySiteChange} locale={locale} data-id="element-6685" />
                {errors.entityId && <p className="mt-1 text-sm text-red-600" data-id="element-6686">{errors.entityId}</p>}
                {errors.siteId && <p className="mt-1 text-sm text-red-600" data-id="element-6687">{errors.siteId}</p>}
              </div>
            </div>
          </div>
        </div>
        <div className="sm:col-span-6" data-id="element-6688">
          <h4 className="text-sm font-medium text-gray-900 mb-3" data-id="element-6689">
            Amount & Period
          </h4>
          <div className="bg-white shadow overflow-hidden sm:rounded-md p-6" data-id="element-6690">
            <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-6691">
              <div className="sm:col-span-2" data-id="element-6692">
                <label htmlFor="totalAmount" className="block text-sm font-medium text-gray-700" data-id="element-6693">
                  Total Amount *
                </label>
                <div className="mt-1 relative rounded-md shadow-sm" data-id="element-6694">
                  <input type="number" name="totalAmount" id="totalAmount" min="0" step="0.01" value={formData.totalAmount} onChange={handleNumberInputChange} className={`focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-7 pr-12 sm:text-sm border-gray-300 rounded-md ${errors.totalAmount ? 'border-red-300' : ''}`} data-id="element-6695" />
                  <div className="absolute inset-y-0 right-0 flex items-center" data-id="element-6696">
                    <label htmlFor="currency" className="sr-only" data-id="element-6697">
                      Currency
                    </label>
                    <div className="h-full rounded-md border-transparent bg-transparent py-0 pl-2 pr-7 text-gray-500 sm:text-sm" data-id="element-6698">
                      {formData.currency}
                    </div>
                  </div>
                </div>
                {errors.totalAmount && <p className="mt-1 text-sm text-red-600" data-id="element-6699">
                    {errors.totalAmount}
                  </p>}
              </div>
              <div className="sm:col-span-2" data-id="element-6700">
                <label htmlFor="currency" className="block text-sm font-medium text-gray-700" data-id="element-6701">
                  Currency
                </label>
                <div className="mt-1" data-id="element-6702">
                  <CurrencySelector value={formData.currency} onChange={handleCurrencyChange} countryCode="" locale={locale} data-id="element-6703" />
                </div>
              </div>
              {isEditing && <div className="sm:col-span-2" data-id="element-6704">
                  <label htmlFor="remainingAmount" className="block text-sm font-medium text-gray-700" data-id="element-6705">
                    Remaining Amount
                  </label>
                  <div className="mt-1 relative rounded-md shadow-sm" data-id="element-6706">
                    <input type="number" name="remainingAmount" id="remainingAmount" min="0" step="0.01" value={formData.remainingAmount} onChange={handleNumberInputChange} className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-7 pr-12 sm:text-sm border-gray-300 rounded-md" data-id="element-6707" />
                    <div className="absolute inset-y-0 right-0 flex items-center" data-id="element-6708">
                      <div className="h-full rounded-md border-transparent bg-transparent py-0 pl-2 pr-7 text-gray-500 sm:text-sm" data-id="element-6709">
                        {formData.currency}
                      </div>
                    </div>
                  </div>
                </div>}
              <div className="sm:col-span-3" data-id="element-6710">
                <label htmlFor="startDate" className="block text-sm font-medium text-gray-700" data-id="element-6711">
                  Start Date *
                </label>
                <div className="mt-1" data-id="element-6712">
                  <input type="date" name="startDate" id="startDate" value={formData.startDate} onChange={handleInputChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md ${errors.startDate ? 'border-red-300' : ''}`} data-id="element-6713" />
                  {errors.startDate && <p className="mt-1 text-sm text-red-600" data-id="element-6714">
                      {errors.startDate}
                    </p>}
                </div>
              </div>
              <div className="sm:col-span-3" data-id="element-6715">
                <label htmlFor="endDate" className="block text-sm font-medium text-gray-700" data-id="element-6716">
                  End Date *
                </label>
                <div className="mt-1" data-id="element-6717">
                  <input type="date" name="endDate" id="endDate" value={formData.endDate} onChange={handleInputChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md ${errors.endDate ? 'border-red-300' : ''}`} data-id="element-6718" />
                  {errors.endDate && <p className="mt-1 text-sm text-red-600" data-id="element-6719">
                      {errors.endDate}
                    </p>}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="sm:col-span-6" data-id="element-6720">
          <h4 className="text-sm font-medium text-gray-900 mb-3" data-id="element-6721">
            Budget Rules
          </h4>
          <div className="bg-white shadow overflow-hidden sm:rounded-md p-6" data-id="element-6722">
            <RuleEditor value={formData.rules} onChange={handleRulesChange} error={errors.rules} data-id="element-6723" />
          </div>
        </div>
        {isEditing && <div className="sm:col-span-6" data-id="element-6724">
            <h4 className="text-sm font-medium text-gray-900 mb-3" data-id="element-6725">
              Lifecycle
            </h4>
            <div className="bg-white shadow overflow-hidden sm:rounded-md p-6" data-id="element-6726">
              <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-6727">
                <div className="sm:col-span-3" data-id="element-6728">
                  <label htmlFor="status" className="block text-sm font-medium text-gray-700" data-id="element-6729">
                    Status
                  </label>
                  <div className="mt-1" data-id="element-6730">
                    <select id="status" name="status" value={formData.status} onChange={handleInputChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-6731">
                      <option value="Active" data-id="element-6732">Active</option>
                      <option value="Archived" data-id="element-6733">Archived</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>}
      </div>
    </form>;
}